<template>
  <div
    class="manager-notifications-ant"
    :class="{ 'is-loading': loading }"
    :aria-busy="loading ? 'true' : undefined"
  >
    <template v-if="!showReplyForm">
      <div class="manager-notifications-ant__table-wrapper personal-area-table-wrapper">
        <a-table
          class="manager-notifications-ant__table"
          :columns="columns"
          :bordered="true"
          :data-source="tableRows"
          :loading="loading"
          :pagination="false"
          :scroll="TABLE_SCROLL"
          :custom-row="getRowProps"
          row-key="id"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'createdAt'">
              {{ formatNotificationDate(record.createdAt) }}
            </template>

            <template v-else-if="column.key === 'recipient'">
              {{ formatRecipient(record.to) }}
            </template>

            <template v-else-if="column.key === 'status'">
              <span
                class="manager-notifications-ant__status"
                :class="`manager-notifications-ant__status--${getManagerNotificationStatusModifier(record.status)}`"
              >
                <span class="manager-notifications-ant__status-dot" aria-hidden="true"></span>
                {{ getManagerNotificationStatusLabel(record.status) }}
              </span>
            </template>
          </template>

          <template #emptyText>
            {{
              errorMessage ||
              (searchQuery.trim() ? 'По вашему запросу ничего не найдено' : 'Уведомлений нет')
            }}
          </template>
        </a-table>
      </div>

      <Pagination
        v-if="nav.totalCount > nav.pageSize"
        v-model="currentPage"
        class="manager-notifications-ant__pagination personal-area-table-pagination"
        :total-count="nav.totalCount"
        :page-size="nav.pageSize"
        @change="handlePageChange"
      />

      <NotificationViewDrawer
        v-model:open="viewDrawerOpen"
        :message="selectedMessage"
        @marked-read="handleNotificationMarkedRead"
      />

      <NotificationCreateDrawer
        v-model:open="createDrawerOpen"
        :recipients="recipients"
        @submit="handleFormSubmit"
      />
    </template>

    <div v-else-if="showReplyForm" class="manager-notifications-ant__reply-form">
      <ReplyForm
        :parent-id="replyParentId"
        :subject="replySubject"
        @submit="handleReplySubmit"
        @cancel="handleReplyCancel"
      />
    </div>

    <ConfirmDialog />
    <Toast />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import { initSearchInput } from '@/js/utils/search-input';
import Pagination from '../Pagination.vue';
import type { ManagerNotificationListItem } from './manager-notification-api';
import NotificationViewDrawer from './NotificationViewDrawer.vue';
import NotificationCreateDrawer from './NotificationCreateDrawer.vue';
import ReplyForm from './ReplyForm.vue';
import { useNotificationData } from './composables/useNotificationData';
import {
  getManagerNotificationStatusLabel,
  getManagerNotificationStatusModifier,
  isReplyNotificationStatus,
} from '../resident-notifications/map-notification';
import {
  MANAGER_LK_NOTIFICATIONS_TAB_ID,
  MANAGER_NOTIFICATIONS_SEARCH_ID,
  NOTIFICATIONS_PAGE_PARAM,
} from './manager-notification-api';

const SEARCH_MIN_LENGTH = 3;
const TABLE_SCROLL = { y: 500 };

type NotificationRow = ManagerNotificationListItem & {
  recipient: string;
  isReply: boolean;
};

const columns: TableColumnsType<NotificationRow> = [
  {
    title: 'Дата / время',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 180,
  },
  {
    title: 'Получатель',
    key: 'recipient',
    dataIndex: 'to',
  },
  {
    title: 'Тема',
    key: 'subject',
    dataIndex: 'subject',
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 160,
  },
];

const {
  loadNotifications,
  loadResidents,
  sendReply,
  loadingNotifications,
  loadingResidents,
  recipients,
  messages,
  nav,
  errorNotifications,
  resetAndLoadFirstPage,
} = useNotificationData();

const loading = computed(() => loadingNotifications.value || loadingResidents.value);
const errorMessage = computed(() => errorNotifications.value);
const viewDrawerOpen = ref(false);
const selectedMessage = ref<ManagerNotificationListItem | null>(null);
const createDrawerOpen = ref(false);
const showReplyForm = ref(false);
const replyParentId = ref(0);
const replySubject = ref('');
const residentId = ref<string | null>(null);
const residentName = ref<string | null>(null);
const searchQuery = ref('');
const currentPage = ref(1);

let cleanupSearchInput: (() => void) | null = null;
let cleanupCreateButton: (() => void) | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSubmittedSearch = '';
let searchReady = false;

const tableRows = computed((): NotificationRow[] => {
  return messages.value.map((message) => ({
    ...message,
    recipient: formatRecipient(message.to),
    isReply: isReplyNotificationStatus(message.status),
  }));
});

watch(
  [recipients, residentId],
  ([recipientsValue, residentIdValue]) => {
    if (!residentIdValue) {
      residentName.value = null;
      return;
    }

    const resident = recipientsValue.residents.find((item) => item.id === residentIdValue);
    residentName.value = resident?.name ?? null;
  },
  { immediate: true },
);

watch(
  [residentName, residentId],
  ([name, id]) => {
    const title = document.querySelector<HTMLElement>('.manager-notifications-section__title');
    if (!title) return;

    title.textContent = id && name ? `Уведомления для ${name}` : 'Уведомления';
  },
  { immediate: true },
);

watch(showReplyForm, (replyVisible) => {
  const toolbar = document.querySelector<HTMLElement>('[data-manager-notifications-toolbar]');
  const title = document.querySelector<HTMLElement>('.manager-notifications-section__title');

  if (toolbar) {
    toolbar.hidden = replyVisible;
  }

  if (title) {
    title.hidden = replyVisible;
  }
});

async function goToAllNotifications() {
  const url = new URL(window.location.href);
  url.searchParams.delete('residentId');
  window.history.replaceState({}, document.title, url.toString());
  residentId.value = null;
  residentName.value = null;
  currentPage.value = 1;
  await resetAndLoadFirstPage(null, searchQuery.value);
}

function formatRecipient(to: string | string[] | undefined): string {
  if (!to) return 'Всем';
  if (Array.isArray(to)) {
    if (to.length === 0) return '—';
    if (to.length === 1) return to[0];
    return `${to.length} резидентов`;
  }
  return to;
}

function formatNotificationDate(iso: string): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const datePart = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${timePart} / ${datePart}`;
}

function getRowProps(record: NotificationRow) {
  return {
    class: record.isReply ? 'manager-notifications-ant__row--reply' : '',
    onClick: () => {
      void onRowClick(record);
    },
  };
}

async function handleFormSubmit() {
  await resetAndLoadFirstPage(residentId.value, searchQuery.value);
}

async function onRowClick(message: NotificationRow) {
  selectedMessage.value = message;
  viewDrawerOpen.value = true;
}

async function handleNotificationMarkedRead() {
  await Promise.all([
    loadNotifications(residentId.value, currentPage.value, searchQuery.value),
    loadResidents(),
  ]);
}

async function handleReplySubmit(message: string) {
  try {
    await sendReply({
      parentId: replyParentId.value,
      message,
      subject: replySubject.value,
    });
    showReplyForm.value = false;
    await resetAndLoadFirstPage(residentId.value, searchQuery.value);
  } catch (error) {
    console.error('Ошибка отправки ответа:', error);
  }
}

function handleReplyCancel() {
  showReplyForm.value = false;
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  syncUrlToBrowser();
  void loadNotifications(residentId.value, page, searchQuery.value);
}

function syncSearchFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  searchQuery.value = params.get('q') ?? '';
  currentPage.value = parsePageFromUrl(params);
  residentId.value = params.get('residentId');
}

function parsePageFromUrl(params: URLSearchParams): number {
  const pageParam = params.get(NOTIFICATIONS_PAGE_PARAM);
  const match = pageParam?.match(/^page-(\d+)$/);
  return match ? Math.max(1, Number(match[1])) : 1;
}

function shouldSubmitSearch(value: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= SEARCH_MIN_LENGTH;
}

function bindSearchInput(): void {
  const elements = initSearchInput(document, { inputId: MANAGER_NOTIFICATIONS_SEARCH_ID });
  if (!elements) {
    return;
  }

  elements.input.value = searchQuery.value;

  const scheduleSearch = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;

    if (!shouldSubmitSearch(searchQuery.value)) {
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
      lastSubmittedSearch = searchQuery.value;
      currentPage.value = 1;
      syncUrlToBrowser();
      void loadNotifications(residentId.value, 1, searchQuery.value);
    }, 400);
  };

  const handleClear = (): void => {
    queueMicrotask(scheduleSearch);
  };

  elements.input.addEventListener('input', scheduleSearch);
  elements.clearBtn.addEventListener('click', handleClear);

  cleanupSearchInput = () => {
    elements.input.removeEventListener('input', scheduleSearch);
    elements.clearBtn.removeEventListener('click', handleClear);
    cleanupSearchInput = null;
  };
}

function bindCreateButton(): void {
  const createBtn = document.querySelector<HTMLButtonElement>('[data-manager-notification-create]');
  if (!createBtn) {
    return;
  }

  const handleCreate = () => {
    createDrawerOpen.value = true;
  };

  createBtn.addEventListener('click', handleCreate);
  cleanupCreateButton = () => {
    createBtn.removeEventListener('click', handleCreate);
    cleanupCreateButton = null;
  };
}

function syncUrlToBrowser(): void {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('tab', String(MANAGER_LK_NOTIFICATIONS_TAB_ID));

  if (residentId.value) {
    url.searchParams.set('residentId', residentId.value);
  }

  const query = searchQuery.value.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (currentPage.value > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${currentPage.value}`);
  }

  window.history.replaceState({ tab: MANAGER_LK_NOTIFICATIONS_TAB_ID }, '', url.toString());
}

onMounted(async () => {
  syncSearchFromUrl();
  bindSearchInput();
  bindCreateButton();

  await Promise.all([
    loadNotifications(residentId.value, currentPage.value, searchQuery.value),
    loadResidents(),
  ]);

  queueMicrotask(() => {
    searchReady = true;
    lastSubmittedSearch = searchQuery.value;
  });
});

onBeforeUnmount(() => {
  cleanupSearchInput?.();
  cleanupCreateButton?.();

  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});
</script>
